/**
 * PathManager
 *
 * Manages gameplay paths in the scene.
 * Paths are registered during level loading and can be looked up by ID.
 */

import { Curve3 } from '@babylonjs/core';
import type { GameplayPath, GameplayPathConfig, GameplayModeType } from '../types/gameplay';
import { SplineConstraint } from './SplineConstraint';
import { logger } from '../core/Logger';

class PathManagerImpl {
  private paths: Map<string, GameplayPath> = new Map();
  private defaultPathId: string | null = null;

  /**
   * Register a gameplay path
   */
  registerPath(
    id: string,
    curve: Curve3,
    mode: GameplayModeType = 'platformer',
    config: GameplayPathConfig = {},
    displayName?: string
  ): GameplayPath {
    const constraint = new SplineConstraint(curve);

    const path: GameplayPath = {
      id,
      displayName: displayName ?? id,
      curve,
      constraint,
      mode,
      config
    };

    this.paths.set(id, path);
    logger.core.info(`PathManager: Registered path "${id}" with mode "${mode}"`);

    // First path becomes default
    if (this.defaultPathId === null) {
      this.defaultPathId = id;
    }

    return path;
  }

  /**
   * Register a path from an existing SplineConstraint
   */
  registerPathFromConstraint(
    id: string,
    constraint: SplineConstraint,
    mode: GameplayModeType = 'platformer',
    config: GameplayPathConfig = {},
    displayName?: string
  ): GameplayPath {
    const path: GameplayPath = {
      id,
      displayName: displayName ?? id,
      curve: constraint.getCurve(),
      constraint,
      mode,
      config
    };

    this.paths.set(id, path);
    logger.core.info(`PathManager: Registered path "${id}" from constraint with mode "${mode}"`);

    if (this.defaultPathId === null) {
      this.defaultPathId = id;
    }

    return path;
  }

  /**
   * Get a path by ID
   * @throws Error if path not found
   */
  getPath(id: string): GameplayPath {
    const path = this.paths.get(id);
    if (!path) {
      throw new Error(`PathManager: Path not found: ${id}`);
    }
    return path;
  }

  /**
   * Try to get a path, returns undefined if not found
   */
  tryGetPath(id: string): GameplayPath | undefined {
    return this.paths.get(id);
  }

  /**
   * Get the default path (first registered)
   */
  getDefaultPath(): GameplayPath | null {
    if (this.defaultPathId === null) return null;
    return this.paths.get(this.defaultPathId) ?? null;
  }

  /**
   * Set the default path ID
   */
  setDefaultPath(id: string): void {
    if (!this.paths.has(id)) {
      throw new Error(`PathManager: Cannot set default to unknown path: ${id}`);
    }
    this.defaultPathId = id;
  }

  /**
   * Get all registered path IDs
   */
  getPathIds(): string[] {
    return Array.from(this.paths.keys());
  }

  /**
   * Check if a path exists
   */
  hasPath(id: string): boolean {
    return this.paths.has(id);
  }

  /**
   * Clear all paths (for level reload)
   */
  clear(): void {
    this.paths.clear();
    this.defaultPathId = null;
    logger.core.info('PathManager: Cleared all paths');
  }

  /**
   * Get world position at T on a path
   */
  getPointAtT(pathId: string, t: number): import('@babylonjs/core').Vector3 {
    const path = this.getPath(pathId);
    return path.constraint.getPointAt(t);
  }

  /**
   * Get the total number of registered paths
   */
  getPathCount(): number {
    return this.paths.size;
  }
}

// Singleton instance
export const PathManager = new PathManagerImpl();
